// GovernancePage — /governance — RE₿EL DAO: vote on the ecosystem with R₿L.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WagmiProvider, useAccount } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ArrowLeft, Vote, Plus, ScrollText, ShieldCheck } from "lucide-react";
import { wagmiConfig } from "../web3/wagmiConfig";
import { API } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { useLanguage } from "../i18n/LanguageContext";
import { tGov } from "../i18n/governance";
import VotingPower from "../components/governance/VotingPower";
import ProposalCard from "../components/governance/ProposalCard";
import ProposalDetail from "../components/governance/ProposalDetail";
import CreateProposalForm from "../components/governance/CreateProposalForm";

const fmt = (n) => Number(n).toLocaleString();

function GovernanceInner() {
  const { lang } = useLanguage();
  const t = (k) => tGov(lang, k);
  const { address, isConnected } = useAccount();

  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [power, setPower] = useState(null);
  const [loadingPower, setLoadingPower] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    const q = address ? `?wallet=${address}` : "";
    fetch(`${API}/governance/proposals${q}`).then((r) => r.json()).then(setData).catch(() => {});
    fetch(`${API}/governance/stats`).then((r) => r.json()).then(setStats).catch(() => {});
  }, [address]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!isConnected || !address) { setPower(null); return; }
    setLoadingPower(true);
    fetch(`${API}/governance/power?wallet=${address}`)
      .then((r) => r.json()).then(setPower).catch(() => {})
      .finally(() => setLoadingPower(false));
  }, [isConnected, address]);

  const proposals = data?.proposals || [];
  const active = proposals.filter((p) => p.status === "active");
  const past = proposals.filter((p) => p.status !== "active");
  const myVotes = data?.my_votes || {};

  return (
    <div className="min-h-screen bg-[#05060B] text-white" data-testid="governance-page">
      <Navbar />
      <section className="mx-auto max-w-5xl px-6 pt-32 pb-24">
        <Link to="/shadow/tokens" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-white/40 hover:text-purple-300" data-testid="gov-back">
          <ArrowLeft className="h-3.5 w-3.5" /> R₿L
        </Link>

        {/* Hero */}
        <div className="mt-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-purple-300/80">
            <Vote className="mr-1.5 inline h-3.5 w-3.5" /> DAO · 1 R₿L = 1 {t("votes").slice(0, -1)} · BASE L2
          </p>
          <h1 className="mt-2 text-4xl font-black tracking-tight sm:text-5xl">{t("title")}</h1>
          <p className="mt-3 max-w-2xl text-base text-white/55">{t("subtitle")}</p>
        </div>

        {/* How it works */}
        <div className="mt-8 rounded-2xl border border-purple-400/20 bg-purple-400/[0.04] p-5" data-testid="gov-how-it-works">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-purple-300/80">
            <ShieldCheck className="mr-1 inline h-3.5 w-3.5" /> {t("howItWorks")}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-white/70">{t("heroText")}</p>
          <ul className="mt-3 grid gap-1.5 md:grid-cols-2">
            {["rule1", "rule2", "rule3", "rule4"].map((k) => (
              <li key={k} className="flex items-start gap-2 text-[11px] text-white/55">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-purple-400" /> {t(k)}
              </li>
            ))}
          </ul>
        </div>

        {/* Voting power / connect */}
        <div className="mt-6">
          <VotingPower power={power} loadingPower={loadingPower} />
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="gov-stats">
          {[
            [t("totalProposals"), stats ? fmt(stats.totalProposals) : "…"],
            [t("activeProposals"), stats ? fmt(stats.activeProposals) : "…"],
            [t("approvedCount"), stats ? fmt(stats.approvedProposals) : "…"],
            [t("totalVotesCast"), stats ? fmt(Number(stats.totalVotesCast)) : "…"],
          ].map(([l, v]) => (
            <div key={l} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{l}</p>
              <p className="mt-1 font-mono text-xl font-black">{v}</p>
            </div>
          ))}
        </div>

        {/* Active proposals */}
        <div className="mt-10 flex items-center justify-between gap-3">
          <h2 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-white/70">
            <ScrollText className="mr-1.5 inline h-4 w-4 text-cyan-300" /> {t("activeProposals")} ({active.length})
          </h2>
          {power?.can_propose && (
            <button onClick={() => setCreating(true)} data-testid="gov-create-btn"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-400 to-fuchsia-500 px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.18em] text-black transition-all hover:-translate-y-0.5">
              <Plus className="h-4 w-4" /> {t("createProposal")}
            </button>
          )}
        </div>
        {isConnected && power && !power.can_propose && (
          <p className="mt-1 font-mono text-[10px] text-white/30">{t("minBalancePropose")}</p>
        )}
        <div className="mt-4 grid gap-4 md:grid-cols-2" data-testid="gov-active-list">
          {active.length === 0 ? (
            <p className="text-sm text-white/40">{t("noActive")}</p>
          ) : (
            active.map((p) => (
              <ProposalCard key={p.id} proposal={p} myVote={myVotes[p.id]} onOpen={setOpenId} />
            ))
          )}
        </div>

        {/* Past proposals */}
        <h2 className="mt-12 font-mono text-xs font-bold uppercase tracking-[0.25em] text-white/70">
          {t("pastProposals")} ({past.length})
        </h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2" data-testid="gov-past-list">
          {past.length === 0 ? (
            <p className="text-sm text-white/40">{t("noPast")}</p>
          ) : (
            past.map((p) => (
              <ProposalCard key={p.id} proposal={p} myVote={myVotes[p.id]} onOpen={setOpenId} />
            ))
          )}
        </div>
      </section>

      {openId && (
        <ProposalDetail proposalId={openId} power={power} onClose={() => setOpenId(null)} onVoted={load} />
      )}
      {creating && (
        <CreateProposalForm onClose={() => setCreating(false)} onCreated={load} />
      )}
    </div>
  );
}

const queryClient = new QueryClient();

export default function GovernancePage() {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <GovernanceInner />
      </QueryClientProvider>
    </WagmiProvider>
  );
}
